import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Upload, Loader2 } from "lucide-react";

export async function scaricaFile(f) {
  try {
    const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a");
    a.href = url; a.download = f.nome || "documento"; a.click(); URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e)); }
}

const ASSEGNO = [["custodia", "In custodia agenzia"], ["consegnato", "Consegnato al venditore"], ["restituito", "Restituito al proponente"]];
const ESITI = [["accettata", "Accettata", "bg-emerald-600"], ["rifiutata", "Rifiutata", "bg-red-600"], ["controproposta", "Controproposta", "bg-amber-500"]];

function Assegno({ p, onDone }) {
  const [st, setSt] = useState(p.assegno?.stato || "custodia");
  const [data, setData] = useState(p.assegno?.data || new Date().toISOString().slice(0, 10));
  const salva = async () => {
    try { await api.post(`/proposte/${p.id}/assegno`, { stato: st, data }); toast.success("Assegno aggiornato"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`assegno-${p.id}`}>
      <span className="font-semibold">Assegno:</span>
      <select data-testid={`assegno-stato-${p.id}`} value={st} onChange={(e) => setSt(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white">
        {ASSEGNO.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input data-testid={`assegno-data-${p.id}`} type="date" value={data} onChange={(e) => setData(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] bg-white" />
      <button data-testid={`assegno-salva-${p.id}`} onClick={salva} className="px-3 py-1.5 rounded-lg bg-[#111111] text-white font-semibold">Salva</button>
      {p.assegno?.at && <span className="text-[var(--muted)]">registrato il {fmtDate(p.assegno.data)}</span>}
    </div>
  );
}

export function PropostaAzioni({ p, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const carica = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post(`/proposte/${p.id}/firmata`, fd); toast.success("Scansione firmata caricata"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const esito = async (stato, label) => {
    if (!window.confirm(`Segnare la proposta come "${label}"?`)) return;
    try { await api.post(`/proposte/${p.id}/esito`, { stato }); toast.success(stato === "accettata" ? "Accettata: preliminare creato, immobile in trattativa, task creati" : `Proposta: ${label}`); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const tornaIndietro = async () => {
    const motivo = window.prompt("Motivo (es. condizione mutuo non avverata). Il preliminare verrà annullato e la provvigione tornerà 'probabile'.", "Condizione mutuo non avverata");
    if (motivo === null) return;
    try { await api.post(`/proposte/${p.id}/torna-indietro`, { motivo }); toast.success("Preliminare annullato"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="w-full mt-2 pt-3 border-t border-[var(--border)] space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`proposta-azioni-${p.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {(p.files || []).map((f) => (
          <button key={f.id} data-testid={`proposta-file-${f.id}`} onClick={() => scaricaFile(f)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[var(--border)] bg-white text-xs font-semibold">
            <FileDown size={13} /> {f.doc_tipo === "proposta_firmata" ? "Firmata" : (f.nome || "").toLowerCase().endsWith(".pdf") ? "PDF" : "DOCX"}
          </button>
        ))}
        <span className="text-[11px] text-[var(--muted)]">Modello {p.modello_nome} v{p.modello_versione}</span>
      </div>
      {p.stato === "bozza" && (
        <>
          <input ref={ref} type="file" className="hidden" data-testid={`firmata-input-${p.id}`} onChange={(e) => carica(e.target.files?.[0])} />
          <button data-testid={`firmata-btn-${p.id}`} disabled={busy} onClick={() => ref.current?.click()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#111111] text-white text-xs font-semibold disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Carica scansione firmata dal proponente
          </button>
        </>
      )}
      {p.stato === "firmata" && (
        <div className="flex gap-2 flex-wrap">
          {ESITI.map(([k, l, c]) => <button key={k} data-testid={`esito-${k}-${p.id}`} onClick={() => esito(k, l)} className={`px-3 py-2 rounded-xl text-white text-xs font-semibold ${c}`}>{l}</button>)}
        </div>
      )}
      {p.stato === "accettata" && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid={`proposta-preliminare-${p.id}`}>
          {p.preliminare_annullato
            ? <span className="font-semibold text-slate-500" data-testid={`proposta-prelim-annullato-${p.id}`}>Preliminare annullato{p.preliminare_annullato_motivo ? `: ${p.preliminare_annullato_motivo}` : ""} · provvigione tornata "probabile"</span>
            : <>
                <Link to="/preliminari" className="font-semibold text-blue-600 underline">Preliminare creato</Link>
                <button data-testid={`torna-indietro-${p.id}`} onClick={tornaIndietro} className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 font-semibold bg-white">Torna indietro (annulla preliminare)</button>
              </>}
        </div>
      )}
      {p.irrevocabile_fino_al && <div className="text-[11px] text-[var(--muted)]">Irrevocabile fino al {fmtDate(p.irrevocabile_fino_al)}{p.rogito_entro ? ` · rogito entro ${fmtDate(p.rogito_entro)}` : ""}</div>}
      <Assegno p={p} onDone={onDone} />
    </div>
  );
}
